import SearchIcon from "~icons/fa6-solid/magnifying-glass";
import ClearIcon from "~icons/fa6-solid/x";
import LoadingIcon from "~icons/fa6-solid/circle-notch";
import { InputHTMLAttributes, useRef, useState } from "react";
import classNames from "classnames";

interface Props
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  onChange: (value: string) => void;
  value: string;
  focusable?: boolean;
  autoFocus?: boolean;
  isFetching?: boolean;
}

export default function Search({
  onChange,
  value = "",
  className = "w-20",
  focusable = false,
  autoFocus = false,
  isFetching = false,
  ...rest
}: Props) {
  const timeoutRef = useRef<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const [input, setInput] = useState(value);
  const containerClassName = focusable
    ? "focus-within:ring-blue-500 focus-within:ring-2 transition-all duration-100"
    : "";

  return (
    <div
      className={`flex gap-2 bg-white rounded-sm ring-gray-300 ring pl-4 h-9 items-center ${containerClassName}`}
    >
      {isFetching ? (
        <LoadingIcon className="animate-spin text-slate-300 size-4" />
      ) : (
        <SearchIcon className="text-slate-300 size-4" />
      )}
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        placeholder="Search..."
        className={classNames("focus:outline-hidden", className)}
        value={input}
        onChange={handleInputChange}
        onKeyUp={(e) => {
          if (e.key === "Escape") {
            handleClear();
          }
        }}
        {...rest}
      />
      <ClearIcon
        className={classNames(
          "size-3 m-3 cursor-pointer text-slate-400 hover:text-slate-600",
          {
            invisible: input.length === 0,
          },
        )}
        onClick={handleClear}
      />
    </div>
  );

  function handleClear() {
    setInput("");
    onChange("");
    inputRef.current?.focus();
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setInput(value);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => onChange(value), 500);
  }
}
